import { useInjection } from 'inversify-react';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { DatasetsUploadDropdown } from '../components/DatasetsUploadDropdown';
import { IImagesUploaderService } from '../modules/datasets/imagesUpload.service';

interface IDatasetUploadProgressData {
  uploadedCount: number;
  uploadedSize: number;
  allCount: number;
  dataSetId: string;
  dataSetName: string;
  hasAnyError: boolean;
  onShowConfirmationModal: () => void;
}

export interface IDatasetsUploadDropdownProps {
  queue: IDatasetUploadProgressData[];
  showModal: boolean;
  onConfirmCancel: () => Promise<void>;
  onResume: () => void;
}

interface IState {
  showModal: boolean;
  datasetId?: string;
  cancelUpload?(id: string): Promise<void>;
  restoreUpload?(id: string): void;
}

export const DatasetsUploadDropdownContainer = observer((): ReactElement => {
  const imageUploaderService = useInjection(IImagesUploaderService.$);

  const [state, setState] = useState<IState>({ showModal: false });

  const progressData = computed((): IDatasetUploadProgressData[] => {
    const imageUploaderProgressData: IDatasetUploadProgressData[] = imageUploaderService.datasetsQueue.map((x) => ({
      uploadedCount: x.uploadedItemsCount,
      uploadedSize: x.uploadedItemsSize,
      allCount: x.allItemsCount,
      dataSetId: x.datasetId,
      dataSetName: x.datasetName,
      hasAnyError: x.hasAnyError,
      onShowConfirmationModal: (): void => {
        showConfirmationModalForFiles(x.datasetId);
      },
    }));

    return imageUploaderProgressData;
  }).get();

  const showConfirmationModalForFiles = (id: string): void => {
    imageUploaderService.pauseUpload(id);
    setState({
      showModal: true,
      datasetId: id,
      restoreUpload: imageUploaderService.restoreUpload,
      cancelUpload: imageUploaderService.cancelUpload,
    });
  };

  const handleConfirmCancel = async (): Promise<void> => {
    if (state.cancelUpload && state.datasetId) {
      await state.cancelUpload(state.datasetId);
    }
    hideModal();
  };

  const handleResume = (): void => {
    if (state.restoreUpload && state.datasetId) {
      state.restoreUpload(state.datasetId);
    }
    hideModal();
  };

  const hideModal = (): void => {
    setState({
      showModal: false,
      datasetId: undefined,
      restoreUpload: undefined,
      cancelUpload: undefined,
    });
  };

  return (
    <DatasetsUploadDropdown
      queue={progressData}
      showModal={state.showModal}
      onResume={handleResume}
      onConfirmCancel={handleConfirmCancel}
    />
  );
});
